import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slide,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  AutoAwesome,
  Check,
  CloudUpload,
  Close,
  Edit,
  Restaurant
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import { fetchWithTimeout, getApiErrorMessage } from '../utils/apiErrors';
import { normalizeImageFiles } from '../utils/imageProcessing';
import './AIFoodAnalyzer.css';
import { macroNames } from '../styles/theme';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const buildLocalDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const divideMacros = (macros, servings) => ({
  kilocalories: (macros?.kilocalories || 0) / servings,
  carbs: (macros?.carbs || 0) / servings,
  protein: (macros?.protein || 0) / servings,
  fat: (macros?.fat || 0) / servings
});

const formatMacroValue = (value) => Math.round((value || 0) * 100) / 100;

const adjustQuantityForServings = (quantity, servings) => {
  if (servings === 1 || !quantity) {
    return quantity;
  }

  const match = `${quantity}`.match(/^(\d+(?:[.,]\d+)?)\s*(.*)$/);
  if (!match) {
    return quantity;
  }

  const amount = parseFloat(match[1].replace(',', '.'));
  const unit = match[2] || '';
  return `${formatMacroValue(amount / servings)} ${unit}`.trim();
};

const adjustDetectedFoodsForServings = (detectedFoods = [], servings) => detectedFoods.map(food => ({
  ...food,
  estimatedQuantity: adjustQuantityForServings(food.estimatedQuantity, servings),
  visibleAmount_g: food.visibleAmount_g ? formatMacroValue(food.visibleAmount_g / servings) : 0,
  hiddenAmount_g: food.hiddenAmount_g ? formatMacroValue(food.hiddenAmount_g / servings) : 0,
  totalAmount_g: food.totalAmount_g ? formatMacroValue(food.totalAmount_g / servings) : 0
}));

const getStoredImages = (meal) => {
  if (!meal?.aiAnalysis) {
    return [];
  }

  if (Array.isArray(meal.aiAnalysis.images) && meal.aiAnalysis.images.length) {
    return meal.aiAnalysis.images.map(image => image.imageUrl).filter(Boolean);
  }

  if (meal.aiAnalysis.imageUrl) {
    return [meal.aiAnalysis.imageUrl];
  }

  return [];
};

const buildEditableEntry = (entry, servings, index) => ({
  localId: entry._id || `entry-${index}`,
  persistedId: entry._id || null,
  aiAnalysis: {
    ...(entry.aiAnalysis || {}),
    dishName: entry.aiAnalysis?.dishName || entry.aiAnalysis?.recipeName || entry.recipeName || `Plato ${index + 1}`,
    recipeName: entry.aiAnalysis?.recipeName || entry.aiAnalysis?.dishName || entry.recipeName || `Plato ${index + 1}`,
    detectedFoods: entry.aiAnalysis?.detectedFoods || []
  },
  originalMacronutrients: entry.originalMacronutrients || entry.aiAnalysis?.originalMacronutrients || entry.estimatedMacronutrients || { kilocalories: 0, carbs: 0, protein: 0, fat: 0 },
  estimatedMacronutrients: entry.estimatedMacronutrients || { kilocalories: 0, carbs: 0, protein: 0, fat: 0 },
  editedMacros: divideMacros(
    entry.finalMacronutrients || entry.userAdjustments || entry.estimatedMacronutrients || { kilocalories: 0, carbs: 0, protein: 0, fat: 0 },
    servings
  ),
  isEditing: false,
  hasManualAdjustments: !!entry.userAdjustments,
  tags: entry.tags || [],
  description: entry.description || ''
});

const AIFoodAnalyzer = ({
  open,
  onClose,
  selectedDate,
  selectedMeal,
  onSuccess,
  existingMeal = null
}) => {
  const { authToken } = useAuth();
  const fileInputRef = useRef(null);

  const isEditMode = !!existingMeal;

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [analysisEntries, setAnalysisEntries] = useState([]);
  const [isPreparingImages, setIsPreparingImages] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [description, setDescription] = useState('');
  const [currentMeal, setCurrentMeal] = useState(selectedMeal || 'Comida');
  const [currentDate, setCurrentDate] = useState(selectedDate || new Date());
  const [servings, setServings] = useState(1);
  const [dragActive, setDragActive] = useState(false);
  const [analysisSessionId, setAnalysisSessionId] = useState(null);
  const [mealBatchId, setMealBatchId] = useState(null);
  const [hasCompletedAnalysis, setHasCompletedAnalysis] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (existingMeal) {
      const initialServings = existingMeal.aiAnalysis?.servings || 1;
      setSelectedFiles([]);
      setImagePreviews(getStoredImages(existingMeal));
      setAnalysisEntries([buildEditableEntry(existingMeal, initialServings, 0)]);
      setDescription(existingMeal.description || '');
      setCurrentMeal(existingMeal.meal || selectedMeal || 'Comida');
      setCurrentDate(existingMeal.date ? new Date(existingMeal.date) : (selectedDate || new Date()));
      setServings(initialServings);
      setAnalysisSessionId(existingMeal.analysisSessionId || null);
      setMealBatchId(existingMeal.mealBatchId || null);
      setHasCompletedAnalysis(false);
      setIsPreparingImages(false);
      setError('');
      return;
    }

    setSelectedFiles([]);
    setImagePreviews([]);
    setAnalysisEntries([]);
    setDescription('');
    setCurrentMeal(selectedMeal || 'Comida');
    setCurrentDate(selectedDate || new Date());
    setServings(1);
    setAnalysisSessionId(null);
    setMealBatchId(null);
    setHasCompletedAnalysis(false);
    setIsPreparingImages(false);
    setError('');
  }, [open, existingMeal, selectedDate, selectedMeal]);

  useEffect(() => {
    if (!open || isEditMode) {
      return;
    }

    if (selectedMeal) {
      setCurrentMeal(selectedMeal);
    }

    if (selectedDate) {
      setCurrentDate(selectedDate);
    }
  }, [open, isEditMode, selectedMeal, selectedDate]);

  const totalImagesLabel = useMemo(() => {
    if (!imagePreviews.length) {
      return 'Sin imágenes';
    }
    return imagePreviews.length === 1 ? '1 imagen' : `${imagePreviews.length} imágenes`;
  }, [imagePreviews.length]);

  const handleDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === 'dragenter' || event.type === 'dragover') {
      setDragActive(true);
    } else if (event.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const addFiles = async (filesList) => {
    if (isPreparingImages || isAnalyzing) {
      return;
    }

    const files = Array.from(filesList || []);
    if (!files.length) {
      return;
    }

    if (files.length > 10) {
      setError('Puedes analizar un máximo de 10 imágenes a la vez.');
      return;
    }

    try {
      setIsPreparingImages(true);
      setError('');
      setImagePreviews([]);
      const normalizedFiles = await normalizeImageFiles(files, {
        maxDimension: 1800,
        quality: 0.82,
        maxOutputBytes: 3 * 1024 * 1024
      });
      const previews = await Promise.all(normalizedFiles.map(file => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = event => resolve(event.target.result);
        reader.onerror = () => reject(new Error('No se pudo previsualizar una de las imágenes.'));
        reader.readAsDataURL(file);
      })));

      setSelectedFiles(normalizedFiles);
      setAnalysisEntries(prevEntries => (isEditMode ? prevEntries : []));
      setImagePreviews(previews);
      setHasCompletedAnalysis(false);
    } catch (preparationError) {
      setSelectedFiles([]);
      setImagePreviews([]);
      setError(preparationError.message || 'No se pudieron preparar las imágenes.');
    } finally {
      setIsPreparingImages(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    addFiles(event.dataTransfer.files);
  };

  const handleImageSelect = (event) => {
    void addFiles(event.target.files);
    event.target.value = '';
  };

  const updateEntry = (localId, updater) => {
    setAnalysisEntries(prevEntries => prevEntries.map(entry => (
      entry.localId === localId ? updater(entry) : entry
    )));
  };

  const handleAnalyze = async () => {
    if (!selectedFiles.length || !currentDate || !currentMeal) {
      setError('Selecciona al menos una imagen, fecha y tipo de comida');
      return;
    }

    setIsAnalyzing(true);
    setError('');

    try {
      const formData = new FormData();
      selectedFiles.forEach(file => formData.append('images', file));
      formData.append('date', buildLocalDateString(currentDate));
      formData.append('meal', currentMeal);
      formData.append('description', description);
      formData.append('tags', '');
      formData.append('servings', `${servings}`);
      formData.append('analysisMode', 'specific_portion');

      const response = await fetchWithTimeout(`${import.meta.env.VITE_API_URL}/unplanned-meals/analyze`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`
        },
        body: formData
      }, 55000);

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Error analizando las imágenes.'));
      }

      const result = await response.json();
      const entries = Array.isArray(result.data?.entries) && result.data.entries.length
        ? result.data.entries
        : [result.data];

      setAnalysisSessionId(result.data.analysisSessionId || null);
      setMealBatchId(entries.length > 1 ? `batch-${Date.now()}` : null);
      setAnalysisEntries(entries.map((entry, index) => buildEditableEntry(entry, servings, index)));
      setHasCompletedAnalysis(true);
    } catch (analysisError) {
      console.error('Error analizando imagen:', analysisError);
      setError(analysisError.message || 'Error analizando la imagen');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleServingsChange = (newServings) => {
    setServings(newServings);
    setAnalysisEntries(prevEntries => prevEntries.map(entry => ({
      ...entry,
      editedMacros: divideMacros(
        entry.hasManualAdjustments
          ? {
            kilocalories: entry.editedMacros.kilocalories * servings,
            carbs: entry.editedMacros.carbs * servings,
            protein: entry.editedMacros.protein * servings,
            fat: entry.editedMacros.fat * servings
          }
          : entry.originalMacronutrients,
        newServings
      )
    })));
  };

  const handleSave = async () => {
    if (!analysisEntries.length) {
      setError('No hay resultados que guardar');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const formData = {
        date: buildLocalDateString(currentDate),
        meal: currentMeal,
        description,
        tags: ''
      };

      if (isEditMode) {
        const entry = analysisEntries[0];
        const response = await fetch(`${import.meta.env.VITE_API_URL}/unplanned-meals/${existingMeal._id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`
          },
          body: JSON.stringify({
            date: formData.date,
            meal: currentMeal,
            description,
            tags: entry.tags || [],
            estimatedMacronutrients: entry.estimatedMacronutrients,
            finalMacronutrients: entry.editedMacros,
            userAdjustments: {
              ...entry.editedMacros,
              notes: ''
            },
            isVerified: true,
            aiAnalysis: {
              ...entry.aiAnalysis,
              detectedFoods: adjustDetectedFoodsForServings(entry.aiAnalysis.detectedFoods, servings),
              servings,
              dishName: entry.aiAnalysis.dishName,
              recipeName: entry.aiAnalysis.recipeName || entry.aiAnalysis.dishName
            },
            analysisSessionId,
            mealBatchId
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Error actualizando la comida');
        }

        const result = await response.json();
        onSuccess?.(Array.isArray(result.items) ? result.items : [result.data]);
        return;
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/unplanned-meals/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`
        },
        body: JSON.stringify({
          formData,
          analysisSessionId,
          mealBatchId,
          entries: analysisEntries.map(entry => ({
            aiAnalysis: {
              ...entry.aiAnalysis,
              dishName: entry.aiAnalysis.dishName,
              recipeName: entry.aiAnalysis.recipeName || entry.aiAnalysis.dishName,
              servings,
              detectedFoods: adjustDetectedFoodsForServings(entry.aiAnalysis.detectedFoods, servings)
            },
            estimatedMacronutrients: entry.editedMacros,
            finalMacronutrients: entry.editedMacros,
            description: description || entry.description,
            tags: entry.tags || [],
            servings,
            userAdjustments: entry.hasManualAdjustments ? {
              ...entry.editedMacros,
              notes: ''
            } : null,
            originalMacronutrients: entry.originalMacronutrients,
            analysisSessionId,
            mealBatchId
          }))
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error guardando la comida');
      }

      const result = await response.json();
      const savedItems = Array.isArray(result.items)
        ? result.items
        : Array.isArray(result.data)
          ? result.data
          : [result.data];

      onSuccess?.(savedItems);
    } catch (saveError) {
      console.error('Error guardando comida:', saveError);
      setError(saveError.message || 'Error guardando la comida');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setImagePreviews([]);
    setAnalysisEntries([]);
    setIsAnalyzing(false);
    setIsSaving(false);
    setError('');
    setDescription('');
    setCurrentMeal(selectedMeal || 'Comida');
    setCurrentDate(selectedDate || new Date());
    setServings(1);
    setDragActive(false);
    setAnalysisSessionId(null);
    setMealBatchId(null);
    setHasCompletedAnalysis(false);
    onClose();
  };

  const macroConfig = {
    kilocalories: { label: macroNames.kilocalories, unit: 'kcal', cardClass: 'macro-card-calories', valueClass: 'macro-value-calories' },
    protein: { label: macroNames.protein, unit: 'g', cardClass: 'macro-card-protein', valueClass: 'macro-value-protein' },
    carbs: { label: macroNames.carbs, unit: 'g', cardClass: 'macro-card-carbs', valueClass: 'macro-value-carbs' },
    fat: { label: macroNames.fat, unit: 'g', cardClass: 'macro-card-fat', valueClass: 'macro-value-fat' }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      TransitionComponent={Transition}
      className="ai-food-analyzer-dialog"
      PaperProps={{
        sx: {
          borderRadius: '24px',
          overflow: 'hidden'
        }
      }}
    >
      <Box className="ai-food-analyzer-header">
        <Box display="flex" alignItems="center" gap={1.5}>
          <AutoAwesome sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h5" fontWeight="700">
              {isEditMode ? 'Editar comida IA' : 'Analizar comida con IA'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {isEditMode
                ? 'Ajusta fecha, comida, plato e información nutricional.'
                : 'Sube una o varias fotos y genera registros separados.'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
            {error}
          </Alert>
        )}

        {!isAnalyzing && !hasCompletedAnalysis && (
          <Box>
            <Box className="step-indicator">
              <Box className="step-number">1</Box>
              <Typography className="step-title">
                {isEditMode ? 'Imágenes del registro' : 'Selecciona una o varias imágenes'}
              </Typography>
            </Box>

            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/avif,image/heic,image/heif,.heic,.heif"
              multiple
              onChange={handleImageSelect}
              ref={fileInputRef}
              disabled={isPreparingImages || isAnalyzing}
              style={{ display: 'none' }}
            />

            {!imagePreviews.length ? (
              <Box
                className={`image-upload-area ${dragActive ? 'drag-over' : ''}`}
                onClick={() => !isPreparingImages && fileInputRef.current?.click()}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                {isPreparingImages ? (
                  <>
                    <CircularProgress size={42} sx={{ color: '#667eea', mb: 2 }} />
                    <Typography variant="h6" fontWeight="600" color="#1e293b" gutterBottom>
                      Optimizando imágenes...
                    </Typography>
                  </>
                ) : (
                  <>
                    <CloudUpload sx={{ fontSize: 48, color: '#667eea', mb: 2 }} />
                    <Typography variant="h6" fontWeight="600" color="#1e293b" gutterBottom>
                      Arrastra tus imágenes aquí
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      o haz clic para seleccionarlas
                    </Typography>
                  </>
                )}
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  Máximo 10 MB por imagen · Se optimizan automáticamente antes del análisis
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Chip label={totalImagesLabel} color="primary" variant="outlined" />
                  <Button size="small" onClick={() => fileInputRef.current?.click()} disabled={isPreparingImages}>
                    {isEditMode ? 'Cambiar imágenes' : 'Cambiar imágenes'}
                  </Button>
                </Box>
                <Grid container spacing={2}>
                  {imagePreviews.map((preview, index) => (
                    <Grid item xs={6} sm={4} md={3} key={`${preview}-${index}`}>
                      <Card className="ai-result-card">
                        <CardMedia
                          component="img"
                          height="140"
                          image={preview}
                          alt={`Imagen ${index + 1}`}
                          sx={{ objectFit: 'cover' }}
                        />
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            <Box className="step-indicator" sx={{ mt: 3 }}>
              <Box className="step-number">2</Box>
              <Typography className="step-title">Contexto y detalles</Typography>
            </Box>

            <TextField
              fullWidth
              label="Pista opcional para la IA"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Ej: Bowl de arroz con salmón y aguacate"
              helperText="También se usará como descripción general del registro"
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': { borderRadius: '12px' }
              }}
            />

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                  <DatePicker
                    label="Fecha"
                    value={currentDate}
                    onChange={(newDate) => setCurrentDate(newDate)}
                    maxDate={new Date()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: { '& .MuiOutlinedInput-root': { borderRadius: '12px' } }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de comida</InputLabel>
                  <Select
                    value={currentMeal || ''}
                    label="Tipo de comida"
                    onChange={(event) => setCurrentMeal(event.target.value)}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value="Desayuno">Desayuno</MenuItem>
                    <MenuItem value="Media mañana">Media mañana</MenuItem>
                    <MenuItem value="Comida">Comida</MenuItem>
                    <MenuItem value="Media tarde">Media tarde</MenuItem>
                    <MenuItem value="Cena">Cena</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ textAlign: 'center', mt: 3, mb: 1 }}>
              <Typography variant="body1" fontWeight="600" color="#1e293b" gutterBottom>
                ¿Para cuántas personas es este meal?
              </Typography>
              <Box display="flex" justifyContent="center" alignItems="center" gap={1.5}>
                <Button variant="outlined" onClick={() => handleServingsChange(Math.max(1, servings - 1))} disabled={servings <= 1}>-</Button>
                <Chip label={`${servings} ${servings === 1 ? 'persona' : 'personas'}`} color="secondary" />
                <Button variant="outlined" onClick={() => handleServingsChange(Math.min(20, servings + 1))}>+</Button>
              </Box>
            </Box>
          </Box>
        )}

        {isAnalyzing && (
          <Box className="analysis-loading">
            <Box sx={{ position: 'relative', mb: 3 }}>
              <CircularProgress size={80} thickness={3} sx={{ color: '#667eea' }} />
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)'
                }}
              >
                <Restaurant sx={{ fontSize: 32, color: '#667eea' }} />
              </Box>
            </Box>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Analizando imágenes...
            </Typography>
            <Typography variant="body2" color="text.secondary" className="loading-pulse">
              Detectando platos, ingredientes y nutrientes
            </Typography>
          </Box>
        )}

        {!!analysisEntries.length && (
          <Box mt={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight="700" color="#1e293b">
                Resultados listos
              </Typography>
              <Chip
                label={analysisEntries.length === 1 ? '1 registro' : `${analysisEntries.length} registros`}
                color="primary"
                variant="outlined"
              />
            </Box>

            <Grid container spacing={3}>
              {analysisEntries.map((entry, entryIndex) => (
                <Grid item xs={12} key={entry.localId}>
                  <Card className="ai-result-card">
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Registro {entryIndex + 1}
                          </Typography>
                          <TextField
                            label="Nombre del plato"
                            value={entry.aiAnalysis.dishName || ''}
                            onChange={(event) => updateEntry(entry.localId, currentEntry => ({
                              ...currentEntry,
                              aiAnalysis: {
                                ...currentEntry.aiAnalysis,
                                dishName: event.target.value,
                                recipeName: event.target.value
                              }
                            }))}
                            size="small"
                            sx={{ mt: 1, minWidth: { xs: '100%', sm: 320 } }}
                          />
                        </Box>
                        <Tooltip title={entry.isEditing ? 'Volver a los valores bloqueados' : 'Ajusta kcal y macros a mano'}>
                          <Button
                            size="small"
                            variant={entry.isEditing ? 'contained' : 'outlined'}
                            color={entry.isEditing ? 'error' : 'primary'}
                            startIcon={entry.isEditing ? <Close /> : <Edit />}
                            onClick={() => updateEntry(entry.localId, currentEntry => ({
                              ...currentEntry,
                              isEditing: !currentEntry.isEditing
                            }))}
                            sx={{
                              mt: 1,
                              alignSelf: 'flex-start',
                              borderRadius: '12px',
                              textTransform: 'none',
                              flexShrink: 0
                            }}
                          >
                            {entry.isEditing ? 'Bloquear valores' : 'Editar macros'}
                          </Button>
                        </Tooltip>
                      </Box>

                      <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                          <Card variant="outlined" sx={{ borderRadius: '16px', overflow: 'hidden' }}>
                            <CardMedia
                              component="img"
                              height="220"
                              image={entry.aiAnalysis.imageUrl || imagePreviews[0]}
                              alt={entry.aiAnalysis.dishName || `Registro ${entryIndex + 1}`}
                              sx={{ objectFit: 'cover', bgcolor: '#f8fafc' }}
                            />
                          </Card>
                        </Grid>

                        <Grid item xs={12} md={8}>
                          <Typography variant="subtitle1" fontWeight="700" gutterBottom>
                            Ingredientes detectados
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                            {(entry.aiAnalysis.detectedFoods || []).map((food, foodIndex) => (
                              <Chip
                                key={`${entry.localId}-${food.name}-${foodIndex}`}
                                label={`${food.name} · ${food.estimatedQuantity || 'estimado'}`}
                                variant="outlined"
                              />
                            ))}
                          </Box>
                          {!!entry.aiAnalysis.analysisLimits?.length && (
                            <Alert severity="info" sx={{ borderRadius: '12px', mb: 2 }}>
                              {entry.aiAnalysis.analysisLimits.join(' · ')}
                            </Alert>
                          )}

                          <Typography variant="subtitle1" fontWeight="700" gutterBottom>
                            Macronutrientes por persona
                          </Typography>
                          <Box className="macro-cards-container">
                            {['kilocalories', 'protein', 'carbs', 'fat'].map(key => {
                              const config = macroConfig[key];
                              const value = entry.editedMacros[key] || 0;
                              return (
                                <Box key={`${entry.localId}-${key}`} className={`macro-card ${config.cardClass}`}>
                                  {entry.isEditing ? (
                                    <TextField
                                      type="number"
                                      value={value}
                                      onChange={(event) => updateEntry(entry.localId, currentEntry => ({
                                        ...currentEntry,
                                        editedMacros: {
                                          ...currentEntry.editedMacros,
                                          [key]: parseFloat(event.target.value) || 0
                                        },
                                        hasManualAdjustments: true
                                      }))}
                                      inputProps={{ step: 0.1, min: 0 }}
                                      size="small"
                                      sx={{ width: '100%', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                    />
                                  ) : (
                                    <Typography className={`macro-value ${config.valueClass}`}>
                                      {formatMacroValue(value)}
                                    </Typography>
                                  )}
                                  <Typography className="macro-label">
                                    {config.label} {config.unit}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={handleClose} disabled={isSaving || isPreparingImages} sx={{ borderRadius: '12px', textTransform: 'none' }}>
          Cancelar
        </Button>

        {!isEditMode && !analysisEntries.length && !isAnalyzing && (
          <Button
            variant="contained"
            onClick={handleAnalyze}
            disabled={isPreparingImages || !selectedFiles.length || !currentDate || !currentMeal}
            className="ai-action-button ai-action-button-primary"
            startIcon={<AutoAwesome />}
          >
            Analizar con IA
          </Button>
        )}

        {isEditMode && selectedFiles.length > 0 && !isAnalyzing && (
          <Button
            variant="outlined"
            onClick={handleAnalyze}
            disabled={isPreparingImages || !currentDate || !currentMeal}
            sx={{ borderRadius: '12px', textTransform: 'none' }}
          >
            Reanalizar con nuevas imágenes
          </Button>
        )}

        {!!analysisEntries.length && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            className="ai-action-button ai-action-button-success"
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <Check />}
          >
            {isSaving
              ? (isEditMode ? 'Actualizando...' : 'Guardando...')
              : (isEditMode ? 'Actualizar comida' : 'Guardar registros')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AIFoodAnalyzer;
